import { useCallback, useMemo, useState } from 'react';
import {
  readConfigurationSnapshot,
  type ConfigurationSnapshot,
} from '../../consumer/configuration-state';
import type {
  MonitoringSummary,
  WarningFilter,
  WarningPage,
  WarningRevision,
} from '../../consumer/contract';
import type { ForecastSummary, ForecastWatch } from '../../consumer/forecasting';
import { useMonitoringRead, type MonitoringReadState } from '../../consumer/polling';
import { riskHref, type EvaluationCounts, type RiskPageProps } from '../../consumer/workbench';
import { MonitoringReadNotice } from '../Summary';
import './overview.css';

export interface OverviewReads {
  summary: MonitoringReadState<MonitoringSummary>;
  warnings: MonitoringReadState<WarningPage>;
  forecast: MonitoringReadState<ForecastSummary>;
  network: MonitoringReadState<ConfigurationSnapshot>;
  counts: MonitoringReadState<EvaluationCounts>;
}
const unavailable = <T,>(): MonitoringReadState<T> => ({
  status: 'ERROR',
  data: null,
  lastReadAt: null,
  error: '此读取端口不可用。',
});
export function isCurrent<T extends { generatedAt: string } | null>(
  state: MonitoringReadState<T>,
  now: number,
): boolean {
  return (
    state.status === 'READY' &&
    state.data !== null &&
    Number.isFinite(Date.parse(state.data.generatedAt)) &&
    now - Date.parse(state.data.generatedAt) <= 5000
  );
}
export function scopeWatches(summary: ForecastSummary | null, areaId: string): ForecastWatch[] {
  return (summary?.watches ?? [])
    .filter((watch) => !areaId || watch.areaId === areaId)
    .sort(
      (a, b) =>
        Number(b.status === 'ACTIVE') - Number(a.status === 'ACTIVE') ||
        b.updatedAt.localeCompare(a.updatedAt),
    );
}
function warningHref(warning: WarningRevision, areaId: string) {
  return riskHref(`/warnings/${encodeURIComponent(warning.warningId)}`, areaId, {
    revision: String(warning.revision),
  });
}
function forecastMetricLabel(metric: string | undefined): string {
  if (metric === 'TEMPERATURE') return '温度';
  if (metric === 'PARTICULATE') return '颗粒物';
  if (metric === 'THERMAL_SCORE') return '热异常评分';
  return '指标未知';
}
/** A watch owns a set of source IDs; array order never identifies a forecast metric. */
export function watchMetricLabels(watch: ForecastWatch, summary: ForecastSummary | null): string {
  const labels = watch.sourceIds.map((id) =>
    forecastMetricLabel(
      summary?.sources.find((source) => source.sourceId === id && source.areaId === watch.areaId)
        ?.metric,
    ),
  );
  return [...new Set(labels.length ? labels : ['指标未知'])].join(' / ');
}
function WatchLinks({
  watch,
  areaId,
  summary,
}: {
  watch: ForecastWatch;
  areaId: string;
  summary: ForecastSummary | null;
}) {
  return (
    <div className="nm-vs04-watch-links">
      {watch.forecastIds.length === 0 ? (
        <span>缺少预测对象引用，详情不可用</span>
      ) : (
        watch.forecastIds.map((id) => {
          // latestForecastId is an exact object relation. Historical IDs may no longer
          // occur here; never infer their metrics from either array's position.
          const source = summary?.sources.find(
            (item) =>
              item.latestForecastId === id &&
              item.areaId === watch.areaId &&
              watch.sourceIds.includes(item.sourceId),
          );
          const metric = forecastMetricLabel(source?.metric);
          const label =
            metric === '指标未知' ? '查看预测详情（指标未知）' : `查看${metric}预测详情`;
          return (
            <a
              key={id}
              title={id}
              aria-label={`${label} · ${id}`}
              href={riskHref(`/forecasts/${encodeURIComponent(id)}`, areaId, {
                horizon: String(watch.horizonSeconds),
              })}
            >
              {label} →
            </a>
          );
        })
      )}
    </div>
  );
}
function Freshness({
  generatedAt,
  current,
}: {
  generatedAt?: string | undefined;
  current: boolean;
}) {
  return (
    <p className={current ? 'nm-vs04-read-time' : 'nm-vs04-delayed'}>
      {generatedAt ? (
        <>
          最后成功数据由后端生成：<time>{generatedAt}</time>
          {!current && ' · 已延迟或读取失败，不能视为当前状态'}
        </>
      ) : (
        '尚无成功后端数据，状态不可用'
      )}
    </p>
  );
}
function Kpi({
  title,
  value,
  note,
  ready,
  metric,
}: {
  title: string;
  value: string | number;
  note: string;
  ready: boolean;
  metric: string;
}) {
  return (
    <article className="nm-vs04-kpi" data-nm-kpi={metric}>
      <span>{title}</span>
      <strong data-nm-value={ready ? value : 'unavailable'}>{ready ? value : '不可用'}</strong>
      <small>{note}</small>
    </article>
  );
}
export function RiskOverviewContent({
  reads,
  areaId,
  now = Date.now(),
}: {
  reads: OverviewReads;
  areaId: string;
  now?: number;
}) {
  const names = new Map(
    reads.network.data?.configuration.areas.map((area) => [area.areaId, area.displayName]),
  );
  const name = (id: string) => names.get(id) ?? id;
  const summaryReady = isCurrent(reads.summary, now);
  const warningReady = isCurrent(reads.warnings, now);
  const forecastReady = isCurrent(reads.forecast, now);
  const countReady = isCurrent(reads.counts, now) && (reads.counts.data?.areaId ?? '') === areaId;
  const networkCurrent =
    reads.network.status === 'READY' &&
    reads.network.data !== null &&
    now - Date.parse(reads.network.data.network.generatedAt) <= 5000;
  const networkAreas =
    reads.network.data?.network.areas.filter((area) => !areaId || area.areaId === areaId) ?? [];
  const coverageReady = networkCurrent && (!areaId || networkAreas.length === 1);
  const enabled = networkAreas.reduce((total, area) => total + area.enabledSources, 0);
  const eligible = networkAreas.reduce((total, area) => total + area.eligibleSources, 0);
  const recent = networkAreas.reduce((total, area) => total + area.recentSources, 0);
  const scopedAreas =
    reads.summary.data?.areas.filter((area) => !areaId || area.areaId === areaId) ?? [];
  const summaryAreaKnown = !areaId || scopedAreas.length === 1;
  const warningCount = areaId
    ? (scopedAreas[0]?.activeWarningCount ?? 0)
    : (reads.summary.data?.activeWarningCount ?? 0);
  const watches = scopeWatches(reads.forecast.data, areaId);
  const warnings =
    reads.warnings.data?.items
      .filter((warning) => (!areaId || warning.areaId === areaId) && warning.state === 'ACTIVE')
      .slice(0, 5) ?? [];
  const changes = [
    ...warnings.map((warning) => ({
      key: warning.warningId,
      time: warning.updatedAt,
      area: warning.areaId,
      title: '观测预警修订更新',
      href: warningHref(warning, areaId),
      current: warningReady,
    })),
    ...watches.map((watch) => ({
      key: watch.watchId,
      time: watch.updatedAt,
      area: watch.areaId,
      title: `预测关注 · ${watch.status}`,
      href: watch.forecastIds[0]
        ? riskHref(`/forecasts/${encodeURIComponent(watch.forecastIds[0])}`, areaId, {
            horizon: String(watch.horizonSeconds),
          })
        : undefined,
      current: forecastReady,
    })),
  ]
    .sort((a, b) => b.time.localeCompare(a.time))
    .slice(0, 3);
  return (
    <>
      <div className="nm-vs04-kpis" data-nm-vs04-scope={areaId || 'all'}>
        <Kpi
          metric="active-warning"
          title="活动观测预警"
          value={warningCount}
          ready={summaryReady && summaryAreaKnown}
          note="规则检出的合成异常；不等于事故确认"
        />
        <Kpi
          metric="forecast-watch"
          title="未来预测关注"
          value={watches.filter((watch) => watch.status === 'ACTIVE').length}
          ready={forecastReady && summaryAreaKnown}
          note="活动预测关注，与观测预警分别统计"
        />
        <Kpi
          metric="source-coverage"
          title="来源健康覆盖"
          value={`${eligible} / ${enabled}`}
          ready={coverageReady}
          note={`合格来源 / 启用来源；近期接收 ${coverageReady ? recent : '未知'}`}
        />
        <Kpi
          metric="pending-evaluation"
          title="预测待核对"
          value={reads.counts.data?.pending ?? 0}
          ready={countReady}
          note="后端目标评估计数；缺少实际数据不视为通过"
        />
      </div>
      <div className="nm-vs04-overview-columns">
        <section className="nm-vs04-card" aria-labelledby="nm-vs04-observed-title">
          <header>
            <div>
              <h2 id="nm-vs04-observed-title">当前观测预警</h2>
              <p>系统已检测到的合成异常，不等于事故确认</p>
            </div>
            <span className="nm-vs04-label nm-vs04-observed">当前观测</span>
          </header>
          <Freshness generatedAt={reads.warnings.data?.generatedAt} current={warningReady} />
          <ul className="nm-vs04-record-list">
            {warnings.map((warning) => (
              <li key={warning.warningId} data-nm-warning-id={warning.warningId}>
                <div>
                  <strong>
                    {name(warning.areaId)} · {warning.hypothesis}
                  </strong>
                  <p>{warning.reason}</p>
                  <small>
                    {warning.areaId} · {warning.updatedAt} · 第 {warning.revision} 版 ·{' '}
                    {warning.acknowledged ? '已读本修订' : '未读本修订'}
                  </small>
                </div>
                <a href={warningHref(warning, areaId)}>查看详情 →</a>
              </li>
            ))}
          </ul>
          {warningReady && warnings.length === 0 && (
            <p className="nm-vs04-empty">当前范围暂无活动观测预警；不表示区域安全。</p>
          )}
          <footer>
            <a href="#nm-vs04-warning-records">查看全部预警与历史记录 →</a>
          </footer>
        </section>
        <section className="nm-vs04-card" aria-labelledby="nm-vs04-predicted-title">
          <header>
            <div>
              <h2 id="nm-vs04-predicted-title">未来预测关注</h2>
              <p>预测可能变化，不代表当前异常已发生</p>
            </div>
            <span className="nm-vs04-label">实验预测</span>
          </header>
          <Freshness generatedAt={reads.forecast.data?.generatedAt} current={forecastReady} />
          <ul className="nm-vs04-record-list">
            {watches.slice(0, 5).map((watch) => (
              <li key={watch.watchId} data-nm-watch-id={watch.watchId}>
                <div>
                  <strong>
                    {name(watch.areaId)} · 未来 +{watch.horizonSeconds} 秒
                    {watchMetricLabels(watch, reads.forecast.data)}关注
                  </strong>
                  <p>{watch.reason}</p>
                  <small>
                    {watch.areaId} · {watch.updatedAt} · {watch.status}
                    {watch.activeObservedWarningId ? ' · 另有关联观测预警' : ''}
                  </small>
                </div>
                <WatchLinks watch={watch} areaId={areaId} summary={reads.forecast.data} />
              </li>
            ))}
          </ul>
          {forecastReady && watches.length === 0 && (
            <p className="nm-vs04-empty">当前范围暂无预测关注；不表示无风险或预测可用。</p>
          )}
          <footer>
            LOCAL_LINEAR_V1 合成持有集评估总体不优于 LAST_VALUE_V1；预测效用仍需改进。
            <a href={riskHref('/evaluation', areaId)}>查看模型效果 →</a>
            <a href="/normal-operations/risk-warning/fire-signals">
              卫星热异常与气象关联（历史研究） →
            </a>
          </footer>
        </section>
      </div>
      <section className="nm-vs04-areas" aria-labelledby="nm-vs04-areas-title">
        <div className="nm-vs04-section-title">
          <h2 id="nm-vs04-areas-title">区域监测概览</h2>
          <a href={riskHref('/network', areaId)}>查看区域与来源配置 →</a>
        </div>
        <Freshness generatedAt={reads.network.data?.network.generatedAt} current={networkCurrent} />
        <div className="nm-vs04-area-cards">
          {networkAreas.map((area) => (
            <article key={area.areaId} className="nm-vs04-card" data-nm-area={area.areaId}>
              <header>
                <h3>{name(area.areaId)}</h3>
                <a href={riskHref('/network', area.areaId)}>查看 →</a>
              </header>
              <div className="nm-vs04-area-counts">
                <span>
                  <strong>
                    {networkCurrent ? `${area.eligibleSources} / ${area.enabledSources}` : '不可用'}
                  </strong>
                  <small>合格 / 启用来源</small>
                </span>
                <span>
                  <strong>
                    {summaryReady
                      ? (reads.summary.data?.areas.find((item) => item.areaId === area.areaId)
                          ?.activeWarningCount ?? '未知')
                      : '不可用'}
                  </strong>
                  <small>观测预警</small>
                </span>
                <span>
                  <strong>
                    {forecastReady
                      ? (reads.forecast.data?.watches.filter(
                          (watch) => watch.areaId === area.areaId && watch.status === 'ACTIVE',
                        ).length ?? 0)
                      : '不可用'}
                  </strong>
                  <small>预测关注</small>
                </span>
              </div>
              <progress
                max={Math.max(1, area.enabledSources)}
                value={networkCurrent ? area.eligibleSources : 0}
                aria-label={`${name(area.areaId)}合格来源覆盖`}
              />
              <p>
                近期接收 {networkCurrent ? area.recentSources : '未知'} · {area.coverage}
                ；来源覆盖并非区域安全程度
              </p>
            </article>
          ))}
        </div>
        {networkCurrent && networkAreas.length === 0 && (
          <p className="nm-vs04-empty">当前范围没有已配置区域；覆盖未知。</p>
        )}
      </section>
      <section className="nm-vs04-card nm-vs04-changes">
        <header>
          <div>
            <h2>最近变化</h2>
            <p>仅保留最新三项后端记录；完整修订在详情中</p>
          </div>
        </header>
        <ul>
          {changes.map((change) => (
            <li key={change.key}>
              <time>{change.time}</time>
              <strong>
                {change.href ? (
                  <a href={change.href}>
                    {name(change.area)} · {change.title}
                  </a>
                ) : (
                  `${name(change.area)} · ${change.title}`
                )}
              </strong>
              {!change.current && <small>最后成功记录 · 已延迟</small>}
            </li>
          ))}
        </ul>
        {changes.length === 0 && <p>尚无可显示的变化记录；请结合读取状态判断。</p>}
      </section>
    </>
  );
}
function WarningRecords({
  port,
  areaId,
  active,
}: Pick<RiskPageProps, 'port' | 'areaId'> & { active: boolean }) {
  const [draft, setDraft] = useState({ state: '' as WarningFilter['state'], from: '', to: '' });
  const [filter, setFilter] = useState<WarningFilter>({
    areaId,
    state: '',
    from: '',
    to: '',
    offset: 0,
    limit: 5,
  });
  const [error, setError] = useState('');
  const applied = useMemo(() => ({ ...filter, areaId }), [filter, areaId]);
  const read = useCallback(
    (signal: AbortSignal) => (active ? port.warnings(applied, signal) : Promise.resolve(null)),
    [port, applied, active],
  );
  const result = useMonitoringRead(`vs04-warning-records:${JSON.stringify(applied)}`, read);
  const page = result.state.data;
  return (
    <section className="nm-vs04-card nm-vs04-history">
      <h2>全部预警与历史记录</h2>
      <p>独立详情保留历史修订。INACTIVE 与已读均不表示区域安全。</p>
      <form
        className="nm-vs04-history-filters"
        onSubmit={(event) => {
          event.preventDefault();
          const from = draft.from ? new Date(draft.from) : null;
          const to = draft.to ? new Date(draft.to) : null;
          if (
            (from && !Number.isFinite(from.getTime())) ||
            (to && !Number.isFinite(to.getTime())) ||
            (from && to && from > to)
          ) {
            setError('开始时间不能晚于结束时间，请输入有效时间。');
            return;
          }
          setError('');
          setFilter({
            ...applied,
            state: draft.state,
            from: from?.toISOString() ?? '',
            to: to?.toISOString() ?? '',
            offset: 0,
          });
        }}
      >
        <label>
          预警状态
          <select
            value={draft.state}
            onChange={(event) =>
              setDraft({ ...draft, state: event.target.value as WarningFilter['state'] })
            }
          >
            <option value="">全部状态</option>
            <option value="ACTIVE">ACTIVE · 活动</option>
            <option value="INACTIVE">INACTIVE · 历史</option>
          </select>
        </label>
        <label>
          开始时间
          <input
            type="datetime-local"
            step="1"
            value={draft.from}
            onChange={(event) => setDraft({ ...draft, from: event.target.value })}
          />
        </label>
        <label>
          结束时间
          <input
            type="datetime-local"
            step="1"
            value={draft.to}
            onChange={(event) => setDraft({ ...draft, to: event.target.value })}
          />
        </label>
        <button type="submit">应用筛选</button>
      </form>
      {error && <p role="alert">{error}</p>}
      <MonitoringReadNotice state={result.state} retry={result.retry} label="预警历史记录" />
      {page && (
        <>
          <Freshness generatedAt={page.generatedAt} current={isCurrent(result.state, Date.now())} />
          <p>
            范围 {areaId || '全部区域'} · 状态 {applied.state || '全部'} · 后端共 {page.total} 条
          </p>
          <ul className="nm-vs04-record-list">
            {page.items.map((warning) => (
              <li key={warning.warningId}>
                <div>
                  <strong>
                    {warning.areaId} · {warning.hypothesis}
                  </strong>
                  <small>
                    {warning.state} · 第 {warning.revision} 版 · {warning.updatedAt}
                  </small>
                </div>
                <a href={warningHref(warning, areaId)}>查看详情 →</a>
              </li>
            ))}
          </ul>
          {page.items.length === 0 && <p>当前筛选没有记录；不表示区域安全。</p>}
          <div className="nm-vs04-pagination">
            <button
              disabled={applied.offset === 0 || result.state.status !== 'READY'}
              onClick={() => setFilter({ ...applied, offset: Math.max(0, applied.offset - 5) })}
            >
              上一页
            </button>
            <span>第 {Math.floor(page.offset / 5) + 1} 页</span>
            <button
              disabled={
                applied.offset + 5 >= page.total ||
                applied.offset >= 100000 ||
                result.state.status !== 'READY'
              }
              onClick={() => setFilter({ ...applied, offset: applied.offset + 5 })}
            >
              下一页
            </button>
          </div>
        </>
      )}
    </section>
  );
}
export function RiskOverview({
  port,
  configurationPort,
  forecastPort,
  countsPort,
  areaId,
}: RiskPageProps) {
  const summaryRead = useCallback((signal: AbortSignal) => port.summary(signal), [port]);
  const warningRead = useCallback(
    (signal: AbortSignal) =>
      port.warnings({ areaId, state: 'ACTIVE', from: '', to: '', offset: 0, limit: 5 }, signal),
    [port, areaId],
  );
  const forecastRead = useCallback(
    (signal: AbortSignal) =>
      forecastPort ? forecastPort.summary(signal) : Promise.reject(new Error('预测端口不可用')),
    [forecastPort],
  );
  const networkRead = useCallback(
    (signal: AbortSignal) =>
      configurationPort
        ? readConfigurationSnapshot(configurationPort, signal)
        : Promise.reject(new Error('配置端口不可用')),
    [configurationPort],
  );
  const countRead = useCallback(
    (signal: AbortSignal) =>
      countsPort
        ? countsPort.read(areaId, signal)
        : Promise.reject(new Error('评估计数端口不可用')),
    [countsPort, areaId],
  );
  const summary = useMonitoringRead(`vs04-summary:${areaId}`, summaryRead);
  const warnings = useMonitoringRead(`vs04-active-warnings:${areaId}`, warningRead);
  const forecast = useMonitoringRead(`vs04-forecast-summary:${areaId}`, forecastRead);
  const network = useMonitoringRead(`vs04-network:${areaId}`, networkRead);
  const counts = useMonitoringRead(`vs04-counts:${areaId}`, countRead);
  const reads = {
    summary: summary.state,
    warnings: warnings.state,
    forecast: forecastPort ? forecast.state : unavailable<ForecastSummary>(),
    network: configurationPort ? network.state : unavailable<ConfigurationSnapshot>(),
    counts: countsPort ? counts.state : unavailable<EvaluationCounts>(),
  };
  const [showRecords, setShowRecords] = useState(false);
  return (
    <div
      className="nm-vs04-overview"
      onClick={(event) => {
        if ((event.target as HTMLElement).closest('a[href="#nm-vs04-warning-records"]'))
          setShowRecords(true);
      }}
    >
      <div className="nm-vs04-scope-toolbar">
        <label>
          当前监测范围
          <select
            aria-label="当前监测范围"
            data-nm-scope
            value={areaId}
            onChange={(event) => window.location.assign(riskHref('', event.target.value))}
          >
            <option value="">全部演练区域</option>
            {areaId &&
              !network.state.data?.configuration.areas.some((area) => area.areaId === areaId) && (
                <option value={areaId}>{areaId}</option>
              )}
            {network.state.data?.configuration.areas.map((area) => (
              <option key={area.areaId} value={area.areaId}>
                {area.displayName}
              </option>
            ))}
          </select>
        </label>
        <button
          onClick={() => {
            summary.retry();
            warnings.retry();
            forecast.retry();
            network.retry();
            counts.retry();
          }}
        >
          刷新状态
        </button>
        <span>按区域聚合 · 后端数据每秒读取</span>
      </div>
      <div className="nm-vs04-read-notices">
        {Object.entries(reads).map(([label, state]) => (
          <MonitoringReadNotice
            key={label}
            state={state as MonitoringReadState<unknown>}
            retry={
              {
                summary: summary.retry,
                warnings: warnings.retry,
                forecast: forecast.retry,
                network: network.retry,
                counts: counts.retry,
              }[label as keyof OverviewReads]
            }
            label={
              {
                summary: '监测摘要',
                warnings: '观测预警',
                forecast: '预测关注',
                network: '来源覆盖',
                counts: '预测待核对',
              }[label as keyof OverviewReads]
            }
          />
        ))}
      </div>
      <RiskOverviewContent reads={reads} areaId={areaId} />
      <details open={showRecords} onToggle={(event) => setShowRecords(event.currentTarget.open)}>
        <summary id="nm-vs04-warning-records">查看全部预警与历史记录</summary>
        <WarningRecords key={areaId} port={port} areaId={areaId} active={showRecords} />
      </details>
    </div>
  );
}
